
import React, {useEffect} from "react";
import {BrowserRouter as Router, Routes, Route, Navigate, useLocation} from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import NProgress from 'nprogress';
import {getUserToken, logout} from "./util/util";
import {AuthenticatedRoutes} from "./layouts/AuthenticatedRoutes";
import {UnAuthenticatedRoutes} from "./layouts/UnAuthenticatedRoutes";
import { useIdleLogout } from "./hooks/useIdleLogout";




import Logo from '../src/assets/img/om-full-primary-logo.svg';
import {UnAuthenticatedRoutesForgotPassword} from "./layouts/UnAuthenticatedRoutesForgotPassword";
import secureLocalStorage from "react-secure-storage";
import { queryClient } from './lib/react-query';
import './index.css';

// Landscape-only overlay for mobile
function LandscapeOnlyOverlay() {
    return (
        <div id="landscape-overlay">
            <div className="landscape-message">
                <div style={{ fontSize: 32, fontWeight: 700, marginBottom: 16 }}>Rotate your device</div>
                <div style={{ fontSize: 18 }}>This app can only be used in landscape mode.</div>
                <div style={{ fontSize: 48, marginTop: 24 }}>🔄</div>
            </div>
            <style>{`
                @media screen and (orientation: portrait) and (max-width: 900px) {
                    #landscape-overlay {
                        display: flex;
                    }
                }
                #landscape-overlay {
                    display: none;
                    position: fixed;
                    z-index: 99999;
                    top: 0; left: 0; right: 0; bottom: 0;
                    background: #fff;
                    align-items: center;
                    justify-content: center;
                    width: 100vw;
                    minHeight: 'auto',
                }
                .landscape-message {
                    text-align: center;
                    color: #111;
                }
                body {
                    overscroll-behavior: none;
                }
            `}</style>
        </div>
    );
}

// (Removed unused ScrollToTop helper - not referenced anywhere)


const AppRoutes = () => (
    <DashboardScrollWrapper>
        <ProgressBarHandler />
        <Routes>
            {/* Root Route - Redirect based on token */}
            <Route path="/" element={<RootRedirect />} />

            {/* Protected Routes - Wrapped with PrivateRoute and dashboard layout */}
            <Route path="/dashboard/*" element={ <PrivateRoute><AuthenticatedRoutes /></PrivateRoute> } />

            {/* Public Routes */}
            <Route path="/onboard/*" element={<UnAuthenticatedRoutes />} />
            <Route path="/forgot-password/*" element={<UnAuthenticatedRoutesForgotPassword />} />

            {/* 404 Page */}
            <Route path="*" element={<NotFound />} />
        </Routes>
    </DashboardScrollWrapper>
);

const App = () => {
    const isAuthenticated = !!getUserToken();

    useIdleLogout({
        timeout: 5 * 60 * 1000,
        onIdle: () => {
            try { logout(); } finally { window.location.href = "/onboard/login"; }
        },
        isAuthenticated,
    });

    return (
        <QueryClientProvider client={queryClient}>
            <Router>
                <LandscapeOnlyOverlay />
                <AppRoutes />
            </Router>
            {/* React Query Devtools - only shows in development */}
            {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />}
        </QueryClientProvider>
    );
}
function DashboardScrollWrapper({ children }) {
    const location = useLocation();
    useEffect(() => {
        if (location.pathname.startsWith('/dashboard')) {
            window.scrollTo(0, 0);
        }
    }, [location.pathname]);
    return children;
}

// Redirect logic for the root path "/"
const RootRedirect = () => {
    const session = getUserToken();

    if (session && !isTokenExpired(secureLocalStorage.getItem('expiresAt'))) {
        return window.location.href = "/dashboard";
    } else {
        logout();
        return <Navigate to="/onboard/login" replace />;
    }
};

const isTokenExpired = (expiresAt) => {
    const currentTime = Date.now();
    return expiresAt && expiresAt < currentTime;
};

// Component to handle protected routes
const PrivateRoute = ({ children }) => {
    const token = getUserToken();

    useEffect(() => {
        if (!token) {
            window.location.href = "/onboard/login"; // Force redirect if token is missing
        }
    }, [token]);

    return token ? children : null; // Prevent rendering protected content without token
};

// ProgressBarHandler Component to handle NProgress
const ProgressBarHandler = () => {
    const location = useLocation();

    useEffect(() => {
        NProgress.start();
        NProgress.done();
        return () => { NProgress.done(); };
    }, [location]);
    return null;
};

// 404 Not Found Page
const NotFound = () => (
    <div style={{ textAlign: "center", marginTop: "10%" }}>
        <img src={Logo} alt="OM Logo"  className='mb-5'/>
        <h1>404 - Page Not Found</h1>
        <a className="btn btn-primary mt-4" href='/dashboard'>Go to Dashboard <i className="bi bi-house"/></a>
    </div>
);

export default App;
